<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ isEdit ? '編輯 FAQ' : '新增 FAQ' }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="goBack">取消</button>
        <button type="button" class="btn btn-primary" :disabled="submitting" @click="handleSubmit">
          <span v-if="submitting"><i class="ri-loader-4-line spin"></i> 儲存中...</span>
          <span v-else>{{ isEdit ? '更新' : '建立' }}</span>
        </button>
      </div>
    </div>

    <div class="page-container">
      <div class="form-card">
        <div class="form-group">
          <label>分類</label>
          <select v-model="formData.category" class="form-control">
            <option value="">無分類</option>
            <option value="營業資訊">營業資訊</option>
            <option value="會員相關">會員相關</option>
            <option value="點數相關">點數相關</option>
            <option value="優惠券相關">優惠券相關</option>
            <option value="訂單相關">訂單相關</option>
          </select>
        </div>

        <div class="form-group">
          <label class="required">問題</label>
          <input
            v-model="formData.question"
            type="text"
            class="form-control"
            placeholder="請輸入問題"
            maxlength="500"
          />
        </div>

        <div class="form-group">
          <label class="required">答案</label>
          <textarea
            v-model="formData.answer"
            class="form-control"
            rows="8"
            placeholder="請輸入答案"
          ></textarea>
        </div>

        <div class="form-group">
          <label>關鍵字</label>
          <input
            v-model="formData.keywords"
            type="text"
            class="form-control"
            placeholder="請輸入關鍵字（用於搜尋）"
          />
          <small class="form-text">多個關鍵字可用逗號分隔。關鍵字用於擴充搜尋範圍，填入同義詞、縮寫或俗稱後，即使搜尋字詞不在問題或答案內文中，仍可找到此 FAQ。</small>
        </div>

        <div class="form-group">
          <label>排序</label>
          <input
            v-model.number="formData.displayOrder"
            type="number"
            class="form-control form-control-narrow"
            min="0"
            placeholder="0"
          />
          <small class="form-text">數字越小越靠前</small>
        </div>

        <div class="form-group">
          <label>啟用</label>
          <label class="switch">
            <input type="checkbox" v-model="formData.enabled" />
            <span class="slider"></span>
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { supportService } from '@/api/modules/support.js';
import { showSuccess, showError } from '@/utils/swal.js';

export default {
  name: 'AutoReplyFaqForm',
  data() {
    return {
      submitting: false,
      formData: {
        category: '',
        question: '',
        answer: '',
        keywords: '',
        displayOrder: 0,
        enabled: true
      }
    };
  },
  computed: {
    isEdit() {
      return !!this.$route.params.id;
    }
  },
  async mounted() {
    if (this.isEdit) {
      await this.loadFaq();
    }
  },
  methods: {
    async loadFaq() {
      try {
        const res = await supportService.getFaq(this.$route.params.id);
        const faq = res.data;
        this.formData = {
          category: faq.category || '',
          question: faq.question || '',
          answer: faq.answer || '',
          keywords: faq.keywords || '',
          displayOrder: faq.displayOrder || 0,
          enabled: faq.enabled !== undefined ? faq.enabled : true
        };
      } catch (e) {
        showError('載入失敗', e.response?.data?.message || '');
        this.goBack();
      }
    },

    async handleSubmit() {
      if (!this.formData.question.trim()) {
        showError('請輸入問題', '');
        return;
      }
      if (!this.formData.answer.trim()) {
        showError('請輸入答案', '');
        return;
      }

      this.submitting = true;
      try {
        if (this.isEdit) {
          await supportService.updateFaq(this.$route.params.id, this.formData);
          await showSuccess('更新成功', '');
        } else {
          await supportService.createFaq(this.formData);
          await showSuccess('建立成功', '');
        }
        this.goBack();
      } catch (e) {
        showError('提交失敗', e.response?.data?.message || '');
      } finally {
        this.submitting = false;
      }
    },

    goBack() {
      this.$router.push({ name: 'AutoReplyFaqs' });
    }
  }
};
</script>

<style scoped>
.page-outer {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.page-header {
  background: #f5f7fa;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 10px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.header-left { display: flex; align-items: center; }
.page-title { font-size: 20px; font-weight: 600; margin: 0; }
.header-actions { display: flex; align-items: center; gap: 8px; }

.page-container {
  padding: 20px;
  padding-top: 72px;
  max-width: 760px;
}

.form-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.form-group { margin-bottom: 16px; }
.form-group:last-child { margin-bottom: 0; }
.form-group label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 6px;
}
.form-group label.required::after { content: ' *'; color: #ef4444; }

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
}
.form-control:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}
.form-control-narrow { max-width: 160px; }

textarea.form-control { resize: vertical; line-height: 1.6; }

.form-text { font-size: 12px; color: #9ca3af; margin-top: 4px; display: block; }

.switch { position: relative; display: inline-block; width: 44px; height: 24px; }
.switch input { opacity: 0; width: 0; height: 0; }
.slider { position: absolute; inset: 0; background: #d1d5db; border-radius: 24px; transition: 0.2s; cursor: pointer; }
.slider::before {
  content: '';
  position: absolute;
  width: 18px;
  height: 18px;
  left: 3px;
  bottom: 3px;
  background: white;
  border-radius: 50%;
  transition: 0.2s;
}
input:checked + .slider { background: #3b82f6; }
input:checked + .slider::before { transform: translateX(20px); }

.btn { padding: 6px 16px; border-radius: 6px; font-size: 13px; font-weight: 500; cursor: pointer; border: none; display: inline-flex; align-items: center; gap: 6px; }
.btn-primary { background: #3b82f6; color: #fff; }
.btn-primary:hover:not(:disabled) { background: #2563eb; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-secondary { background: #f3f4f6; color: #374151; border: 1px solid #e5e7eb; }
.btn-secondary:hover { background: #e5e7eb; }

.spin { animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
</style>
